import { adapter, getScriptTag } from '@seek/tealium-adapter';

import {
  getBrand,
  getCountryCode,
  getLocale,
  getZone,
} from 'src/core/config/globalStates';
import { getPageAnalyticsName, type PageName } from 'src/core/utils/pageUtil';
import type { Environment } from 'src/types/clientContext';

import { getAuthentication } from '../authentication/authentication';
import logger from '../logger/logger';

import {
  AnalyticsInteractions,
  AnalyticsPageSections,
  AnalyticsPageViewEvents,
  type AnalyticsEvents,
} from './analyticsEvents';

const tealiumAdapter = adapter();

export const analyticsScriptTag = (environment: Environment) => {
  const enableAnalytics =
    environment === 'staging' || environment === 'production';
  if (!enableAnalytics) {
    return '';
  }

  return getScriptTag({
    account: 'seek',
    profile: 'candidate-main',
    environment: getAnalyticsEnvironment(environment),
  });
};

const getAnalyticsEnvironment = (environment: Environment) => {
  switch (environment) {
    case 'development':
      return 'development';
    case 'staging':
      return 'qa';
    case 'production':
      return 'prod';
    default:
      return 'development';
  }
};

const getCommonPayload = async () => {
  const userAuth = await getAuthentication();
  const { authenticationStatus, userProfile: { id: userId } = {} } = userAuth;
  const loggedIn: boolean = authenticationStatus === 'authenticated';

  const commonPayload = {
    siteCountry: getCountryCode(),
    siteLanguage: getLocale(),
    zone: getZone(),
    brand: getBrand(),
    isLoggedin: loggedIn,
    ...(loggedIn &&
      userId && {
        loginId: userId,
      }),
  };

  return commonPayload;
};

export const trackPageView = async (page: PageName, payload?: object) => {
  const viewEventName = AnalyticsPageViewEvents[page];
  if (!viewEventName) return;

  const currentPage = getPageAnalyticsName(page);
  const currentPageSection = AnalyticsPageSections[page];
  const common = await getCommonPayload();
  const pageFields = {
    ...common,
    ...(currentPage && {
      currentPage,
    }),
    ...(currentPageSection && {
      currentPageSection,
    }),
    ...payload,
  };

  logger.info('trackPageView', { page, pageFields });

  tealiumAdapter.view(viewEventName, pageFields);
};

export const trackInteraction = async (
  page: PageName,
  event: AnalyticsEvents,
  payload?: object,
) => {
  const { event: eventName, interaction } = AnalyticsInteractions[event];
  const currentPage = getPageAnalyticsName(page);
  const currentPageSection = AnalyticsPageSections[page];

  const common = await getCommonPayload();
  const pageFields = {
    ...common,
    ...(currentPage && {
      currentPage,
    }),
    ...(currentPageSection && {
      currentPageSection,
    }),
    interaction,
    ...payload,
  };

  logger.info('trackInteraction', { pageFields });

  tealiumAdapter.view(eventName, pageFields);
};
